import React, { useState } from "react";
import { Divider, Popover, Segmented, Tooltip } from "antd";
import { LuInfo, LuMonitor, LuMoon, LuPalette, LuSun } from "react-icons/lu";
import CustomButton from "../components/commonComponents/button";
import { BRANDS, DEFAULT_BRAND, DEFAULT_MODE, THEME_MODES } from "./themeConfig";
import { useTheme } from "./ThemeProvider";

// Lucide SVGs sit on the text baseline; nudge them onto the label's centre line.
const MODE_ICON_CLASS = "align-[-0.15em]";
const MODE_OPTIONS = [
  { value: THEME_MODES.LIGHT, label: "Light", icon: <LuSun className={MODE_ICON_CLASS} /> },
  { value: THEME_MODES.DARK, label: "Dark", icon: <LuMoon className={MODE_ICON_CLASS} /> },
  {
    value: THEME_MODES.SYSTEM,
    label: "System",
    icon: <LuMonitor className={MODE_ICON_CLASS} />,
  },
];

const SectionLabel = ({ children, aside }) => (
  <div className="mb-2 flex items-center justify-between text-sm">
    <span className="font-medium text-heading">{children}</span>
    {aside && <span className="text-tertiary">{aside}</span>}
  </div>
);

// One brand: its own primary + accent, so the colours are data here (not styling).
const BrandSwatch = ({ brandKey, label, primary, accent, selected, onSelect }) => (
  <Tooltip title={label}>
    <button
      type="button"
      aria-label={`${label} brand`}
      aria-pressed={selected}
      onClick={() => onSelect(brandKey)}
      className={`h-7 w-7 cursor-pointer rounded-full border-0 p-0 outline-offset-2 transition-transform hover:scale-110 ${
        selected ? "outline outline-2 outline-primary" : ""
      }`}
      style={{ background: `linear-gradient(135deg, ${primary} 55%, ${accent} 55%)` }}
    />
  </Tooltip>
);

const ThemePanel = () => {
  const { brand, preferredMode, setMode, setBrand, isDarkAvailable } = useTheme();
  const isDefault = preferredMode === DEFAULT_MODE && brand === DEFAULT_BRAND;

  return (
    <div className="flex w-[264px] flex-col">
      <SectionLabel>Appearance</SectionLabel>
      <Segmented block value={preferredMode} onChange={setMode} options={MODE_OPTIONS} />
      {!isDarkAvailable && preferredMode !== THEME_MODES.LIGHT && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-tertiary">
          <LuInfo className="mt-0.5 shrink-0" />
          <span>
            Dark mode isn&apos;t available on this page yet. It applies on supported pages.
          </span>
        </div>
      )}

      <Divider className="!my-4" />

      <SectionLabel aside={BRANDS[brand]?.label}>Brand colour</SectionLabel>
      <div className="flex flex-wrap gap-3 px-0.5 py-1">
        {Object.entries(BRANDS).map(([key, { label, primary, accent }]) => (
          <BrandSwatch
            key={key}
            brandKey={key}
            label={label}
            primary={primary}
            accent={accent}
            selected={key === brand}
            onSelect={setBrand}
          />
        ))}
      </div>

      <Divider className="!my-4" />

      <div>
        <CustomButton
          type="link"
          size="small"
          className="!p-0"
          btnText="Reset to default"
          disabled={isDefault}
          handleClick={() => {
            setMode(DEFAULT_MODE);
            setBrand(DEFAULT_BRAND);
          }}
        />
      </div>
    </div>
  );
};

/**
 * Header theme control: one button opening a panel with appearance
 * (light / dark / system) and brand colour. Renders nothing unless the theme
 * preview is enabled (see THEME_PREVIEW_ENABLED in themeConfig.js).
 */
const ThemeSwitcher = () => {
  const { isPreviewEnabled } = useTheme();
  const [open, setOpen] = useState(false);
  // Controlled rather than blanking the title while the panel is open: an empty
  // title stops antd tracking hover, so the tooltip never saw the pointer leave
  // and popped back up, stranded, once the panel closed.
  const [tooltipOpen, setTooltipOpen] = useState(false);
  if (!isPreviewEnabled) return null;

  return (
    <Tooltip title="Theme" open={tooltipOpen && !open} onOpenChange={setTooltipOpen}>
      <span>
        <Popover
          open={open}
          onOpenChange={next => {
            setOpen(next);
            setTooltipOpen(false);
          }}
          trigger="click"
          placement="bottomRight"
          arrow={false}
          content={<ThemePanel />}
        >
          <span>
            <CustomButton
              type="text"
              shape="circle"
              icon={<LuPalette className="text-lg text-secondary" />}
              ariaLabel="Theme settings"
            />
          </span>
        </Popover>
      </span>
    </Tooltip>
  );
};

export default ThemeSwitcher;
